// Opportunities: Liste, Pipeline-Board, Anlegen und Ändern (Runde 5, Agent 5D): Typen und Client nach crm/API.md, Abschnitt 20.5.
// Gebaut von 5D: Antworten von Anlegen und Ändern tragen `hinweise` (Sätze für die Anzeige, siehe API.md 20.5).
import { http } from "./client";
import type { Liste, ListenParameter, Opportunity, OpportunityStatus, Pipeline } from "./types";

/** Opportunity wie auf der Lead-Seite (`Opportunity` in types.ts) plus Namen für die Anzeige. */
export interface OpportunityZeile extends Opportunity {
  lead_name: string | null;
  contact_name: string | null;
  user_name: string | null;
  /** Close-Farbe des Status; fehlt sie, zeigt die Oberfläche nach Typ: aktiv gelb, gewonnen grün, verloren rot */
  status_color?: string | null;
  date_created: string | null;
  date_updated: string | null;
  /** additiv (5D): Jahreswert in Cent, Lead-Status, Pipeline-Name */
  annualized_value?: number;
  lead_status_label?: string | null;
  pipeline_name?: string | null;
}

/** Summen in Cent, wie Close "annualisiert" (monatlich mal 12, einmalig und jährlich unverändert). */
export interface OpportunitySumme {
  aktiv: number;
  gewonnen: number;
  verloren: number;
}

export interface OpportunityListe extends Liste<OpportunityZeile> {
  summe: OpportunitySumme;
  /** Zahl je Typ über die ganze gefilterte Menge (additiv, 5D) */
  anzahl?: OpportunitySumme;
}

/** Antwort von Anlegen und Ändern: die Opportunity und Hinweise zur Anzeige (z. B. "Lead hat schon eine aktive Opportunity"). */
export interface OpportunitySchreiben {
  opportunity: OpportunityZeile;
  hinweise?: string[];
}

export interface BoardSpalte {
  status: OpportunityStatus & { color?: string | null };
  anzahl: number;
  /** annualisierte Summe der Spalte in Cent */
  summe: number;
  /** die ersten Karten; weitere über `opportunitiesApi.liste({status_id, cursor})` */
  karten: OpportunityZeile[];
  has_more: boolean;
  cursor: string | null;
}

export interface Board {
  /** gewählte Pipeline; null, wenn es keine gibt */
  pipeline: Pipeline | null;
  pipelines: Pipeline[];
  spalten: BoardSpalte[];
}

export type OpportunitySortierung =
  | "value" | "-value" | "confidence" | "-confidence" | "close_date" | "-close_date"
  | "date_created" | "-date_created" | "date_updated" | "-date_updated" | "user" | "-user" | "lead" | "-lead";

export interface OpportunityFilter {
  pipeline_id?: string;
  user_id?: string;
  smart_view_id?: string;
  /** Zeitraum bezieht sich auf das Abschlussdatum (Close date) oder die Anlage */
  feld?: "close" | "created";
  von?: string;
  bis?: string;
}

export interface OpportunityListenParameter extends ListenParameter, OpportunityFilter {
  status_id?: string[];
  status_type?: "active" | "won" | "lost";
  lead_id?: string;
  q?: string;
  sort?: OpportunitySortierung;
}

export interface OpportunityEingabe {
  lead_id: string;
  /** ohne Angabe: erster aktiver Status der Pipeline */
  status_id?: string | null;
  /** Cent */
  value?: number | null;
  value_period?: "one_time" | "monthly" | "annual" | null;
  confidence?: number | null;
  /** Abschlussdatum (Close date), YYYY-MM-DD */
  date_won?: string | null;
  contact_id?: string | null;
  /** Zuständiger; ohne Angabe der Handelnde */
  user_id?: string | null;
  note?: string | null;
}

export type OpportunityAenderung = Partial<Omit<OpportunityEingabe, "lead_id">>;

export const opportunitiesApi = {
  liste: (p: OpportunityListenParameter = {}, signal?: AbortSignal) =>
    http.get<OpportunityListe>(
      "/api/opportunities",
      {
        limit: p.limit, cursor: p.cursor, pipeline_id: p.pipeline_id, status_id: p.status_id, status_type: p.status_type, user_id: p.user_id,
        lead_id: p.lead_id, smart_view_id: p.smart_view_id, feld: p.feld, von: p.von, bis: p.bis, q: p.q, sort: p.sort,
      },
      signal,
    ),
  board: (p: OpportunityFilter & { status_type?: "active" | "all"; sort?: OpportunitySortierung; je_spalte?: number } = {}, signal?: AbortSignal) =>
    http.get<Board>("/api/opportunities/board", { ...p }, signal),
  eine: (id: string) => http.get<{ opportunity: OpportunityZeile }>(`/api/opportunities/${encodeURIComponent(id)}`),
  anlegen: (eingabe: OpportunityEingabe) => http.post<OpportunitySchreiben>("/api/opportunities", eingabe),
  /** ändern; auch Statuswechsel (Ziehen im Board = genau ein Statuswechsel) */
  aendern: (id: string, aenderung: OpportunityAenderung) =>
    http.patch<OpportunitySchreiben>(`/api/opportunities/${encodeURIComponent(id)}`, aenderung),
};
